import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Header from 'components/Header';
import { Footer } from 'components/Footer';
import { useToast } from 'components/toast/ToastProvider';
import { PATHS } from 'routes/paths';
import { authService } from 'services/auth/authService';
import { userService } from 'services/user/userService';
import { DashboardProfile, Payment, Subscription } from 'types/user';
import { getErrorMessage } from 'utils/http';
import { authStorage } from 'utils/storage';
import { hasDashboardAccess } from 'utils/subscription';

type AccountFormData = {
  firstName: string;
  lastName: string;
  displayName: string;
  email: string;
  currentPass: string;
  newPass: string;
  confirmPass: string;
};

type Errors = {
  firstName?: string;
  lastName?: string;
  confirmPass?: string;
};

const EMPTY_DASHBOARD: DashboardProfile = {
  firstname: '',
  lastname: '',
  email: '',
  plan_id: null,
  payments: [],
  subscriptions: [],
};

const formatDashboardStatus = (status: string) => (
  status === 'pending_activation' ? 'PENDING' : status.replace(/_/g, ' ')
);

export default function UserDashboard() {
  const [activeTab, setActiveTab] = useState('Payments');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<Errors>({});
  const [dashboard, setDashboard] = useState<DashboardProfile>(EMPTY_DASHBOARD);
  const [accountData, setAccountData] = useState<AccountFormData>({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    currentPass: '',
    newPass: '',
    confirmPass: '',
  });

  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const stripedBg = {
    backgroundImage:
      'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)',
  };

  const initials = useMemo(() => {
    const first = accountData.firstName.trim().charAt(0);
    const last = accountData.lastName.trim().charAt(0);
    return `${first}${last}`.toUpperCase() || 'U';
  }, [accountData.firstName, accountData.lastName]);

  const navItems = [
    { id: 'Payments', label: 'Payments', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg> },
    { id: 'My subscriptions', label: 'My subscriptions', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path></svg> },
    { id: 'Account Details', label: 'Account Details', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg> },
    { id: 'Change Password', label: 'Change Password', icon: <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg> },
  ];

  useEffect(() => {
    const token = authStorage.getToken();

    if (!token) {
      showError('Please log in to access your account.');
      navigate(PATHS.login);
      return;
    }

    const loadDashboard = async () => {
      try {
        const response = await userService.getDashboard();
        const profile = response.data;

        authStorage.setSubscriptionActive(hasDashboardAccess(profile));
        setDashboard(profile);
        setAccountData((current) => ({
          ...current,
          firstName: profile.firstname,
          lastName: profile.lastname,
          displayName: `${profile.firstname}${profile.lastname ? ` ${profile.lastname}` : ''}`.trim(),
          email: profile.email,
        }));
      } catch (error) {
        authStorage.setSubscriptionActive(false);
        showError(getErrorMessage(error, 'Unable to load your profile.'));
      } finally {
        setIsLoading(false);
      }
    };

    void loadDashboard();
  }, [navigate, showError]);

  const validateProfile = () => {
    const newErrors: Errors = {};

    if (!accountData.firstName.trim()) newErrors.firstName = 'Required';
    if (!accountData.lastName.trim()) newErrors.lastName = 'Required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors: Errors = {};

    if (accountData.newPass !== accountData.confirmPass) {
      newErrors.confirmPass = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateProfile()) {
      showError('Please fix the highlighted profile fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await userService.updateProfile({
        firstname: accountData.firstName.trim(),
        lastname: accountData.lastName.trim(),
        display_name: accountData.displayName.trim(),
      });

      setDashboard((current) => ({
        ...current,
        firstname: accountData.firstName.trim(),
        lastname: accountData.lastName.trim(),
      }));
      setAccountData((current) => ({
        ...current,
        firstName: accountData.firstName.trim(),
        lastName: accountData.lastName.trim(),
        displayName: accountData.displayName.trim(),
      }));
      showSuccess(response.message || 'Profile updated successfully');
    } catch (error) {
      showError(getErrorMessage(error, 'Unable to update your profile.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!accountData.currentPass || !accountData.newPass) {
      showError('Please enter your current and new password.');
      return;
    }

    if (!validatePassword()) {
      showError('Please fix the password confirmation field.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authService.changePassword({
        old_password: accountData.currentPass,
        new_password: accountData.newPass,
      });

      setAccountData((current) => ({
        ...current,
        currentPass: '',
        newPass: '',
        confirmPass: '',
      }));
      setErrors({});
      showSuccess(response.message || 'Password updated successfully');
    } catch (error) {
      showError(getErrorMessage(error, 'Unable to update your password.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPaymentStatus = (payment: Payment) => {
    const linkedSubscription = dashboard.subscriptions.find(
      (subscription) => subscription.payment_reference === payment.payment_id
    );
    const statusLabel = formatDashboardStatus(linkedSubscription?.status || payment.status);

    return (
      <span className="text-[10px] font-black uppercase text-slate-700 bg-slate-100 px-2.5 py-1 rounded-md">
        {statusLabel}
      </span>
    );
  };

  const renderSubscriptionStatus = (subscription: Subscription) => {
    const statusLabel = formatDashboardStatus(subscription.status);

    return (
      <span className="text-xs sm:text-sm font-bold text-slate-700">{statusLabel}</span>
    );
  };

  return (
    <div style={stripedBg} className="min-h-screen w-full pt-2 pb-4 px-2 sm:py-8 sm:px-4 md:px-8 lg:px-12 flex justify-center font-sans text-slate-900 overflow-hidden">
      <div className="w-full bg-white rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden border border-gray-200 flex flex-col shadow-sm relative">
        <Header />

        <main className="flex-grow pt-10 pb-6 px-4 sm:pt-14 sm:pb-10 md:pt-16 md:pb-12 md:px-10 lg:px-20 w-full overflow-hidden">
          <div className="max-w-6xl mx-auto mb-6 sm:mb-10 flex flex-col lg:flex-row items-center justify-between gap-6 bg-gray-50 p-5 sm:p-6 rounded-2xl md:rounded-3xl border border-gray-100 w-full">
            <div className="flex items-center gap-4 sm:gap-5 w-full lg:w-auto">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-[#2b3543] flex-shrink-0 flex items-center justify-center text-white text-xl sm:text-2xl font-black shadow-lg">
                {initials}
              </div>
              <div className="overflow-hidden">
                <h1 className="text-xl sm:text-2xl font-black text-[#2b3543] truncate">{accountData.displayName || 'My Account'}</h1>
                <p className="text-slate-500 font-medium text-xs sm:text-sm truncate">{accountData.email || 'Loading profile...'}</p>
              </div>
            </div>

            <nav className="flex w-full lg:w-auto gap-2 p-1.5 bg-gray-100 rounded-2xl border border-gray-200 overflow-x-auto snap-x [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex-shrink-0 snap-start flex items-center gap-2 px-4 py-3 sm:py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${
                    activeTab === item.id ? 'bg-white text-[#2b3543] shadow-sm' : 'text-slate-500 hover:text-[#2b3543]'
                  }`}
                >
                  <span className={activeTab === item.id ? 'text-[#2b3543]' : 'text-slate-400'}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </nav>
          </div>

          <section className="max-w-6xl mx-auto bg-white border border-gray-200 rounded-[1.5rem] md:rounded-[2.5rem] p-5 sm:p-8 md:p-14 shadow-sm min-h-[400px] md:min-h-[500px] w-full">
            {isLoading ? (
              <div className="flex min-h-[320px] items-center justify-center text-sm font-bold uppercase tracking-[0.2em] text-slate-400">
                Loading dashboard...
              </div>
            ) : null}

            {!isLoading && activeTab === 'Payments' && (
              <div className="animate-in fade-in duration-500">
                <h2 className="text-xl sm:text-2xl font-black text-[#2b3543] mb-6 sm:mb-8 flex items-center gap-3">
                  <div className="w-1.5 h-6 bg-[#2b3543] rounded-full"></div> Payments
                </h2>
                <div className="overflow-x-auto w-full border border-gray-100 rounded-2xl">
                  <table className="w-full text-left min-w-[600px]">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="p-4 text-[10px] font-black uppercase text-slate-400 whitespace-nowrap">Payment ID</th>
                        <th className="p-4 text-[10px] font-black uppercase text-slate-400 whitespace-nowrap">Date</th>
                        <th className="p-4 text-[10px] font-black uppercase text-slate-400 whitespace-nowrap">Status</th>
                        <th className="p-4 text-[10px] font-black uppercase text-slate-400 whitespace-nowrap">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {dashboard.payments.length ? (
                        dashboard.payments.map((payment) => (
                          <tr key={payment.payment_id}>
                            <td className="p-4 text-xs sm:text-sm font-bold text-[#2b3543] whitespace-nowrap">{payment.payment_id}</td>
                            <td className="p-4 text-xs sm:text-sm text-slate-500 whitespace-nowrap">{new Date(payment.date).toLocaleDateString()}</td>
                            <td className="p-4 whitespace-nowrap">{renderPaymentStatus(payment)}</td>
                            <td className="p-4 text-xs sm:text-sm font-bold text-slate-700 whitespace-nowrap">Rs. {payment.price}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="p-8 text-center text-sm font-medium text-slate-500">No payments found yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {!isLoading && activeTab === 'My subscriptions' && (
              <div className="animate-in fade-in duration-500 space-y-10 sm:space-y-12">
                <div className="flex flex-col gap-px bg-gray-100 border border-gray-100 rounded-2xl overflow-hidden">
                  {dashboard.subscriptions.length ? (
                    dashboard.subscriptions.map((subscription) => (
                      <div key={subscription.id} className="grid gap-px">
                        <div className="flex justify-between items-center p-4 bg-gray-50 gap-4"><span className="text-xs sm:text-sm font-bold text-slate-500">Plan</span><span className="text-xs sm:text-sm font-bold text-[#2b3543]">{subscription.plan_name}</span></div>
                        <div className="flex justify-between items-center p-4 bg-white gap-4"><span className="text-xs sm:text-sm font-bold text-slate-500">Status</span>{renderSubscriptionStatus(subscription)}</div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 gap-4"><span className="text-xs sm:text-sm font-bold text-slate-500">Start Date</span><span className="text-xs sm:text-sm font-bold text-[#2b3543]">{new Date(subscription.starts_at).toLocaleDateString()}</span></div>
                        <div className="flex justify-between items-center p-4 bg-white gap-4"><span className="text-xs sm:text-sm font-bold text-slate-500">End Date</span><span className="text-xs sm:text-sm font-bold text-[#2b3543]">{new Date(subscription.ends_at).toLocaleDateString()}</span></div>
                        <div className="flex justify-between items-center p-4 bg-gray-50 gap-4"><span className="text-xs sm:text-sm font-bold text-slate-500">Price</span><span className="text-xs sm:text-sm font-bold text-[#2b3543]">Rs. {subscription.price}</span></div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-sm font-medium text-slate-500 bg-white">No subscriptions found yet.</div>
                  )}
                </div>
              </div>
            )}

            {!isLoading && activeTab === 'Account Details' && (
              <form onSubmit={handleSaveProfile} className="animate-in slide-in-from-bottom-4 duration-500 px-2 space-y-6 sm:space-y-8">
                <h2 className="text-xl sm:text-2xl font-black text-[#2b3543] mb-6 sm:mb-8 flex items-center gap-3">
                  <div className="w-1.5 h-6 bg-[#2b3543] rounded-full"></div> Account Details
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500">First Name</label>
                    <input type="text" value={accountData.firstName} onChange={e => setAccountData({ ...accountData, firstName: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2b3543] outline-none transition-colors" />
                    {errors.firstName && <p className="text-xs font-bold text-red-500">{errors.firstName}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500">Last Name</label>
                    <input type="text" value={accountData.lastName} onChange={e => setAccountData({ ...accountData, lastName: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2b3543] outline-none transition-colors" />
                    {errors.lastName && <p className="text-xs font-bold text-red-500">{errors.lastName}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500">Display Name</label>
                  <input type="text" value={accountData.displayName} onChange={e => setAccountData({ ...accountData, displayName: e.target.value })} className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:border-[#2b3543] outline-none transition-colors" />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500">Email / Username*</label>
                  <input type="email" value={accountData.email} className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-slate-400 cursor-not-allowed" disabled />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500">Plan ID</label>
                  <input type="text" value={dashboard.plan_id ?? 'No plan selected'} className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-3 text-sm text-slate-400 cursor-not-allowed" disabled />
                </div>

                <button className="w-full sm:w-auto bg-[#2b3543] text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg active:scale-95 text-center block disabled:opacity-70" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            )}

            {!isLoading && activeTab === 'Change Password' && (
              <form onSubmit={handlePasswordSave} className="animate-in slide-in-from-bottom-4 duration-500  space-y-6 sm:space-y-8">
                <h2 className="text-xl sm:text-2xl font-black text-[#2b3543] mb-6 sm:mb-8 flex items-center gap-3 uppercase tracking-wider">
                  <div className="w-1.5 h-6 bg-[#2b3543] rounded-full"></div> Password Change
                </h2>

                <div className="space-y-5">
                  <div className="space-y-2">
                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">Current password (leave blank to leave unchanged)</label>
                    <input type="password" value={accountData.currentPass} onChange={e => setAccountData({ ...accountData, currentPass: e.target.value })} placeholder="Enter current password" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:border-[#2b3543] outline-none transition-colors" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">New password (leave blank to leave unchanged)</label>
                    <input type="password" value={accountData.newPass} onChange={e => setAccountData({ ...accountData, newPass: e.target.value })} placeholder="Enter new password" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 text-sm focus:border-[#2b3543] outline-none transition-colors" />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] sm:text-xs font-bold text-slate-400 uppercase tracking-widest">Confirm new password</label>
                    <input type="password" value={accountData.confirmPass} onChange={e => setAccountData({ ...accountData, confirmPass: e.target.value })} placeholder="Confirm new password" className={`w-full bg-gray-50 border rounded-xl px-4 py-3.5 text-sm outline-none transition-colors ${errors.confirmPass ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 focus:border-[#2b3543]'}`} />
                    {errors.confirmPass && <p className="text-[10px] font-bold text-red-500">{errors.confirmPass}</p>}
                  </div>
                </div>

                <div className="pt-2">
                  <button className="w-full sm:w-auto bg-[#2b3543] text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-800 transition-all shadow-lg active:scale-95 text-center block disabled:opacity-70" disabled={isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </div>
  );
}
